import _ from 'lodash';
import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import Fuse from 'fuse.js';
import Icon from '@mdi/react';
import { mdiClose, mdiEmailArrowRightOutline, mdiMagnify } from '@mdi/js';
import { allOccurrences, isUserRecipient, Occurrence, Recipient, UserRecipient } from './types';
import { UserAvatar } from '../../Avatar';
import { Select } from '../../Select/Select';
import { IconButton } from '../../Button/IconButton';

const occurrenceOptions = allOccurrences.map((occurrence) => ({
	label: occurrence.charAt(0).toUpperCase() + occurrence.slice(1),
	value: occurrence,
}));

interface ShareSearchbarProps {
    users: UserRecipient[];
	recipients: Recipient[],
	onRecipientsChange: (recipients: Recipient[]) => void;
	initialOccurrence: Occurrence;
	onOccurrenceSelect: (occurrence: Occurrence) => void;
}

export function ShareSearchbar({
	users,
	recipients,
	onRecipientsChange,
	initialOccurrence,
	onOccurrenceSelect,
}: ShareSearchbarProps) {
	const fuse = useRef<Fuse<UserRecipient>>(null);
	useEffect(() => {
		if (users) {
			fuse.current = new Fuse(users, {
				keys: ['firstName', 'lastName', 'email'],
				includeScore: true,
				useExtendedSearch: true,
				minMatchCharLength: 1,
				sortFn: (a, b) => a.score! - b.score!,
			});
		}
	}, [users]);

	const input = useRef<HTMLInputElement>(null);
	const [searchHasFocus, setSearchHasFocus] = React.useState(false);
	const [searchStr, setSearchStr] = React.useState('');
	const [searchIsValidEmail, setSearchIsValidEmail] = React.useState(false);
	const [searchResults, setSearchResults] = React.useState<UserRecipient[]>(null);

	const placeholder = recipients.length ? 'add other users or e-mails and press enter' : 'Type a name or an e-mail and press enter';
	// Recommendations or results
	const displayResults: Recipient[] = _.differenceBy(searchResults || users, recipients, 'id').slice(0, 3);

	const search = useCallback((value: string) => {
		if (value) {
			// Not putting a limit here in case a recipient is already selected
			const results = fuse.current?.search(value);
			setSearchResults(results?.map(({ item }) => item));
		} else {
			setSearchResults(null);
		}
	}, [fuse, setSearchResults]);

	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchStr(e.target.value);
		setSearchIsValidEmail(e.target.value && input.current?.checkValidity());
		search(e.target.value);
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		switch (e.key) {
			case 'Escape':
				input.current?.blur();
				break;
			case 'Enter':
				if (searchStr && searchIsValidEmail && !_.find(recipients, { email: searchStr })) {
					const newRecipient: Recipient = { email: searchStr };
					onRecipientsChange([...recipients, newRecipient]);
					setSearchStr('');
					setSearchResults(null);
				}
				break;
			case 'Backspace':
				if (searchStr === '') {
					onRecipientsChange(recipients.slice(0, -1));
				}
				break;
			default:
				break;
		}
	};

	const handleDeleteTag = (toDelete: Recipient) => {
		onRecipientsChange(recipients.filter((recipient) => recipient.email !== toDelete.email));
	};

	const handleBlur = () => {
		setSearchHasFocus(false);
	};

	const handleFocus = () => {
		setSearchHasFocus(true);
	};

	const handleResultMouseDown = (e: React.MouseEvent<HTMLButtonElement>, newRecipient: Recipient, clearSearch: boolean = false) => {
		e.preventDefault(); // prevent blur
		const isUser = isUserRecipient(newRecipient);
		if (isUser || searchIsValidEmail) {
			onRecipientsChange([...recipients, newRecipient]);
			if (clearSearch) {
				setSearchStr('');
				setSearchResults(null);
			}
		}
		input.current?.focus();
	};

	const handleClearSearch = () => {
		setSearchStr('');
		setSearchResults(null);
	};

	return (
		<div className="flex flex-col items-center gap-2 self-stretch rounded-sm">
			<div className={clsx(
				'flex py-2 px-4 justify-between items-center self-stretch',
				'rounded-sm border border-solid border-borders-primary',
				'hover:border-grey-400 hover:shadow-[0px -1px 3px 0px var(--color-grey-100) inset]',
				'focus-within:border-grey-400',
			)}>
				<div className="flex items-start gap-2 flex-[1_0_0]">
					<div
						className={clsx(
							'flex pr-2 items-center gap-[15px] flex-[1_0_0] min-h-6',
							(searchStr || recipients.length) && 'border-r border-solid border-borders-primary',
						)}
					>
						<Icon path={mdiMagnify} size="18px" className="text-fg-primary flex-shrink-0" />
						<div className="flex flex-col items-start gap-[10px] flex-[1_0_0]">
							{recipients.map((recipient) => (
								<RecipientTag
									recipient={recipient}
									onDelete={handleDeleteTag}
									key={recipient.email}
								/>
							))}
							<div className="flex items-center flex-[1_0_0] self-stretch">
								<input
									ref={input}
									type="email"
									placeholder={placeholder}
									value={searchStr}
									onChange={handleSearchChange}
									onKeyDown={handleKeyDown}
									onFocus={handleFocus}
									onBlur={handleBlur}
									className="w-full bg-transparent outline-hidden text-grey-800 placeholder-text-tertiary focus:placeholder-opacity-0 font-normal text-sm"
								/>
								{searchStr && (
									<IconButton
										icon={<Icon path={mdiClose} />}
										onClick={handleClearSearch}
										className="flex p-1 items-start gap-2.5"
										size="xs"
									/>
								)}
							</div>
						</div>
					</div>
					{(searchStr || recipients.length > 0) && (
						<Select
							value={initialOccurrence}
							onChange={(newOccurrence) => onOccurrenceSelect(newOccurrence)}
							options={occurrenceOptions}
							variant="borderless"
						/>
					)}
				</div>
			</div>
			{searchHasFocus && (
				<div className={clsx(
					'flex p-4 flex-col items-start gap-4 self-stretch',
					'rounded-lg border-1 border-solid border-borders-primary',
					'bg-bg-interactive-light-bg-active_default',
				)}>
					<div className="flex items-center self-stretch">
						<span className="text-sm font-semibold text-text-primary tracking-[0.14px]">
							{searchResults ? 'Results' : 'Recommendations'}
						</span>
					</div>
					<div className="flex flex-col items-start gap-2.5 self-stretch">
						{searchStr && ( // Don't show when search is empty
							<RecipientResult
								recipient={{ email: searchStr } as Recipient}
								onAddRecipient={(e, recipient) => handleResultMouseDown(e, recipient, true)}
								disabled={!searchIsValidEmail}
							/>
						)}
						{displayResults.map((recipient) => (
							<RecipientResult
								key={isUserRecipient(recipient) ? recipient.id : recipient.email}
								recipient={recipient}
								onAddRecipient={handleResultMouseDown}
							/>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

function RecipientTag({ recipient, onDelete }: {
	recipient: Recipient,
	onDelete: (toDelete: Recipient) => void,
}) {
	const isUser = isUserRecipient(recipient);
	return (
		<div className={clsx(
			'flex items-start',
			!isUser && 'self-stretch',
		)}>
			<div className="flex py-0.5 pl-2 pr-1 items-center gap-1 rounded-sm bg-grey-70">
				<span className="text-xs text-text-primary tracking-[0.12px]">
					{isUser ? `${recipient.firstName} ${recipient.lastName}` : recipient.email}
				</span>
				<IconButton
					icon={<Icon path={mdiClose} />}
					onClick={() => onDelete(recipient)}
					className={clsx(
						'flex p-1 items-start gap-2.5 rounded-1',
						'hover:bg-grey-70',
						'active:bg-grey-150',
					)}
					size="xs"
				/>
			</div>
		</div>
	);
}

function RecipientResult({ recipient, onAddRecipient, disabled = false }: {
	recipient: Recipient,
	onAddRecipient: (e: React.MouseEvent<HTMLButtonElement>, r: Recipient) => void,
	disabled?: boolean,
}) {
	const isUser = isUserRecipient(recipient);
	const Wrapper = disabled ? 'div' : 'button';
	const emailAtIdx = recipient.email.indexOf('@');
	const showDomain = emailAtIdx === -1 || recipient.email.indexOf('.', emailAtIdx + 1) === -1
	return (
		<Wrapper
			onMouseDown={(e) => onAddRecipient(e, recipient)}
			className={clsx(
				'flex p-2 items-start gap-2 self-stretch',
				'rounded-sm border-1 border-solid border-borders-primary',
				disabled ? 'bg-bg-interactive-light-bg-inactive_default' : 'bg-bg-interactive-light-bg-active_default',
				!disabled && 'hover:bg-bg-interactive-light-bg-active_hover hover:border-borders-primary_hover',
				!disabled && 'active:bg-bg-interactive-light-bg-active_pressing active:border-borders-primary_pressed',
			)}
		>
			{isUser ? (
				<>
					<UserAvatar userId={recipient.id} className="flex size-6 p-1 items-start" />
					<div className="flex flex-col justify-center items-start flex-[1_0_0]">
						<span className="text-sm font-semibold text-text-secondary tracking-[0.14px]">{recipient.firstName} {recipient.lastName}</span>
						<span className="text-sm font-normal overflow-ellipsis overflow-hidden text-text-tertiary tracking-[0.14px]">{recipient.email}</span>
					</div>
				</>
			) : (
				<div className="flex items-center gap-3 flex-[1_0_0]">
					<Icon path={mdiEmailArrowRightOutline} size="20px" />
					<div className="flex items-start flex-[1_0_0]">
						<span className="text-sm text-text-primary">{recipient.email}</span>
						{disabled && (
							<span className="text-sm text-text-tertiary italic">{`${emailAtIdx === -1 ? '@' : ''}${showDomain ? 'domain.' : ''}com`}</span>
						)}
					</div>
				</div>
			)}
		</Wrapper>
	);
}
